<template>
  <b-card>
    <div class="flex flex-col gap-6" v-if="user">
      <!-- Header: Avatar, Name, Status -->
      <div class="flex flex-col gap-3 -mb-2">
        <div class="flex items-center gap-4 lg:gap-3 xl:gap-4">
          <b-avatar
            :url="avatarUrl"
            alt="profile image"
            size="2xl"
            :initials="initials"
          />
          <div class="flex flex-col min-w-0 flex-1">
            <h1 class="text-2xl lg:text-xl xl:text-2xl font-bold text-white break-words min-w-0">{{ fullName }}</h1>
            <!-- Status Badge — hidden temporarily, re-enable when availability feature is needed
            <b-badge v-if="user.profile?.available_to_work" variant="success" size="sm" class="mt-1 w-fit">
              {{ t('profile_info.part_time_search') }}
            </b-badge>
            <b-badge v-else variant="danger" size="sm" class="mt-1 w-fit">
              {{ t('profile_info.unavailable') }}
            </b-badge>
            -->
          </div>
        </div>
        <b-button v-if="isOwner" light size="xs" block @click="openContactEditModal" data-e2e="profile-edit-info-button">
          <span class="inline-flex items-center gap-1.5">
            <Icon name="solar:pen-bold-duotone" size="1rem" />
            {{ t('bProfileInformationCard.editProfile') }}
          </span>
        </b-button>
      </div>

      <b-divider class="-my-2" />

      <!-- Presentation Video -->
      <div class="flex flex-col gap-3 -mt-2">
        <div v-if="hasVideo && isOwner" class="flex justify-end gap-2">
          <b-button light size="xs" @click="confirmDeleteVideo = true">
            <Icon name="solar:trash-bin-trash-bold-duotone" size="0.875rem" />
          </b-button>
          <b-button light size="xs" :loading="isUploading" @click="openVideoUpload">
            <Icon name="solar:pen-bold-duotone" size="0.875rem" />
          </b-button>
        </div>
        <div v-if="hasVideo" class="rounded-lg overflow-hidden">
          <video
            controls
            preload="metadata"
            class="w-full rounded-lg"
            :src="videoUrl"
          />
        </div>
        <div v-else-if="isOwner" class="rounded-lg border border-gray-700 border-dashed flex flex-col items-center justify-center py-8 px-4 gap-4">
          <div class="w-12 h-12 rounded-full bg-gray-600 flex items-center justify-center">
            <Icon name="solar:clapperboard-open-linear" size="1.5rem" class="text-gray-500" />
          </div>
          <b-button size="sm" :loading="isUploading" class="max-w-full !h-auto min-h-[32px] py-1.5" @click="openVideoUpload">
            <Icon name="solar:upload-minimalistic-bold" size="1rem" class="mr-2 shrink-0" />
            {{ t('profile_video.add_video') }}
          </b-button>
          <p class="text-xs text-gray-500 text-center">{{ t('profile_video.constraints') }}</p>
        </div>
      </div>

      <!-- Info List -->
      <div class="flex flex-col gap-4">
        <!-- Title / Role -->
        <div class="flex justify-between items-start gap-2 border-b border-[#373737] pb-2">
            <span class="text-white font-medium text-xl lg:text-lg xl:text-xl break-words min-w-0" data-e2e="profile-title-value">{{ user.profile?.title || t('profile_info.title_not_defined') }}</span>
        </div>

        <!-- TJM -->
        <div class="flex justify-between items-center gap-2">
            <span class="text-gray-400 shrink-0">{{ t('profile_info.daily_rate') }}</span>
            <div class="flex items-center gap-2 min-w-0">
                 <span class="text-white font-bold truncate" data-e2e="profile-daily-rate-value">{{ user.profile?.daily_rate ? `${user.profile?.daily_rate}€` : 'N/A' }}</span>
            </div>
        </div>
      </div>
      
      <b-divider />

       <div class="flex flex-col gap-2 min-w-0">
            <span class="text-gray-400">{{ t('profile_info.location') }}</span>
            <span class="text-white break-words" data-e2e="profile-location-value">{{ user.profile?.location || t('profile_info.not_specified') }}</span>
       </div>

        <div class="flex flex-col gap-2">
            <span class="text-gray-400">{{ t('profile_info.skills') }}</span>
             <div class="flex flex-wrap gap-2">
                <b-badge v-for="skill in displayedSkills" :key="skill.id" pill variant="skill">{{ skill.name }}</b-badge>
             </div>
             <button
               v-if="skills.length > MAX_SKILLS_DISPLAYED"
               class="text-sm text-primary hover:text-primary/80 transition-colors text-left cursor-pointer"
               @click="showAllSkills = !showAllSkills"
             >
               {{ showAllSkills ? t('profile_info.show_less') : t('profile_info.show_more_skills', { count: skills.length - MAX_SKILLS_DISPLAYED }) }}
             </button>
        </div>

        <div class="flex flex-col gap-2" v-if="orderedProfileLanguages.length">
            <span class="text-gray-400">{{ t('profile_info.languages') }}</span>
             <div class="flex flex-wrap gap-2">
                <b-badge v-for="code in orderedProfileLanguages" :key="code" pill variant="skill">{{ t('languages.' + code) }}</b-badge>
             </div>
        </div>


       <div v-if="isProfilePublic" class="mt-4 grid grid-cols-4 gap-2 items-center w-full">
            <!-- Owner: privacy toggle | Non-owner: contact button -->
            <b-checkbox-toggle
              v-if="isOwner"
              :options="privacyOptions"
              v-model="privacyValue"
              @change="onPrivacyChange"
              data-e2e="profile-privacy-toggle"
              class="col-span-3"
            />
            <b-button v-else-if="hasAnyContact" block :class="canFavorite ? 'col-span-2' : 'col-span-3'" @click="openContactModal = true">
              <span class="inline-flex items-center gap-2">
                <Icon name="solar:letter-bold-duotone" size="1.25rem" />
                {{ t('profile_info.contact') }}
              </span>
            </b-button>
            <!-- Favorite button — visible to logged-in non-owners -->
            <button
              v-if="canFavorite"
              class="flex items-center justify-center py-2.5 rounded-lg border transition-colors cursor-pointer col-span-1"
              :class="isExpertFavorite ? 'border-primary bg-primary/20 hover:bg-primary/30' : 'border-white/20 text-white hover:bg-white/10'"
              :title="isExpertFavorite ? t('profile_info.remove_favorite') : t('profile_info.add_favorite')"
              data-e2e="profile-favorite-button"
              @click="onToggleFavorite"
            >
              <Icon
                :name="isExpertFavorite ? 'solar:bookmark-bold' : 'solar:bookmark-linear'"
                size="1.25rem"
                :class="isExpertFavorite ? 'text-primary' : ''"
              />
            </button>
            <!-- Share icon button — always visible -->
            <button
              class="flex items-center justify-center py-2.5 rounded-lg border border-white/20 text-white hover:bg-white/10 transition-colors cursor-pointer col-span-1"
              @click="openShareModal = true"
              data-e2e="profile-share-button"
            >
              <Icon name="solar:share-bold" size="1.25rem" />
            </button>
       </div>
       <!-- Owner with private profile: show toggle without share -->
       <div v-else-if="isOwner" class="mt-4">
            <b-checkbox-toggle
              :options="privacyOptions"
              v-model="privacyValue"
              @change="onPrivacyChange"
              data-e2e="profile-privacy-toggle"
            />
       </div>

    </div>
  </b-card>

  <!-- Video upload input (hidden) -->
  <input
    ref="videoFileInput"
    type="file"
    accept="video/mp4,video/webm,video/quicktime"
    class="hidden"
    @change="onVideoFileSelected"
  />

  <!-- Video delete confirmation modal -->
  <b-modal v-model="confirmDeleteVideo" :title="t('profile_video.delete')" size="sm">
    <template #default>
      <p class="px-10 pb-6 text-gray-300">{{ t('profile_video.delete_confirm') }}</p>
    </template>
    <template #footer>
      <div class="flex justify-end gap-3 px-10 pb-8">
        <b-button light @click="confirmDeleteVideo = false">{{ t('common.cancel') }}</b-button>
        <b-button @click="onDeleteVideo" :loading="isDeletingVideo">{{ t('profile_video.delete') }}</b-button>
      </div>
    </template>
  </b-modal>

  <b-modal v-model="openShareModal" :title="$t('bProfileInformationCard.shareProfile')" data-e2e="profile-share-modal">
    <div class="px-10">
      <div class="flex flex-col gap-5">
        <b-text-field :model-value="urlToCopy" :label="$t('bProfileInformationCard.copyProfileLink')" readonly data-e2e="profile-share-url-input" />
      </div>
    </div>
  </b-modal>

  <b-modal v-model="openContactModal" :title="t('contact_expert.title')" no-footer size="md">
    <div class="px-10 pb-8 flex flex-col items-center gap-6">
      <!-- Phone -->
      <div v-if="user?.profile?.contact_phone" class="flex items-center gap-3">
        <span class="text-gray-300">{{ t('contact_expert.by_phone') }}</span>
        <a :href="`tel:${user.profile.contact_phone}`" class="inline-flex items-center gap-2 bg-zinc-700 hover:bg-zinc-600 text-white px-4 py-2 rounded-lg transition-colors">
          <Icon name="solar:phone-bold-duotone" size="1.25rem" />
          {{ user.profile.contact_phone }}
        </a>
      </div>

      <!-- Divider "Ou" -->
      <div v-if="user?.profile?.contact_phone && (user?.profile?.contact_email || user?.profile?.whatsapp || user?.profile?.linkedin)" class="flex items-center gap-4 w-full">
        <div class="flex-1 border-t border-zinc-600" />
        <span class="text-gray-400 text-sm">{{ t('contact_expert.or') }}</span>
        <div class="flex-1 border-t border-zinc-600" />
      </div>

      <!-- Platforms -->
      <div v-if="user?.profile?.contact_email || user?.profile?.whatsapp || user?.profile?.linkedin">
        <p class="text-gray-400 text-sm text-center mb-4">{{ t('contact_expert.platforms') }}</p>
        <div class="flex flex-wrap justify-center gap-3">
          <a v-if="user?.profile?.contact_email" :href="`mailto:${user.profile.contact_email}`" target="_blank" rel="noopener noreferrer" class="inline-flex items-center gap-2 bg-zinc-700 hover:bg-zinc-600 text-white px-6 py-3 rounded-lg transition-colors">
            <Icon name="solar:letter-bold-duotone" size="1.25rem" />
            Email
          </a>
          <a v-if="user?.profile?.whatsapp" :href="`https://wa.me/${user.profile.whatsapp.replace(/[^0-9+]/g, '')}`" target="_blank" rel="noopener noreferrer" class="inline-flex items-center gap-2 bg-zinc-700 hover:bg-zinc-600 text-white px-6 py-3 rounded-lg transition-colors">
            <Icon name="mdi:whatsapp" size="1.25rem" />
            WhatsApp
          </a>
          <a v-if="user?.profile?.linkedin" :href="user.profile.linkedin" target="_blank" rel="noopener noreferrer" class="inline-flex items-center gap-2 bg-zinc-700 hover:bg-zinc-600 text-white px-6 py-3 rounded-lg transition-colors">
            <Icon name="mdi:linkedin" size="1.25rem" />
            LinkedIn
          </a>
        </div>
      </div>
    </div>
  </b-modal>

  <!-- Profile edit modal -->
  <b-modal v-model="contactEditModalOpen" :title="t('profile_contact.edit_title')" no-footer size="md">
    <div class="px-10 pb-8 flex flex-col gap-5">
      <b-file-upload
        v-model="selectedPhoto"
        :loading="contactSaving"
        :url="user?.profile?.signed_picture_url || ''"
        data-e2e="profile-modal-photo"
      />

      <b-divider />

      <div class="flex gap-4">
        <b-text-field
          v-model="contactForm.first_name"
          :label="t('profile_info.first_name_label')"
          :placeholder="t('profile_info.first_name_placeholder')"
          class="flex-1"
        />
        <b-text-field
          v-model="contactForm.last_name"
          :label="t('profile_info.last_name_label')"
          :placeholder="t('profile_info.last_name_placeholder')"
          class="flex-1"
        />
      </div>
      <b-text-field
        v-model="contactForm.title"
        :label="t('profile_info.title_label')"
        :placeholder="t('profile_info.title_placeholder')"
      />
      <b-text-field
        v-model="contactForm.daily_rate"
        :label="t('profile_info.daily_rate')"
        :placeholder="t('profile_info.daily_rate_placeholder')"
      />
      <b-location-autocomplete
        v-model="contactForm.location"
        :label="t('profile_info.location')"
        :placeholder="t('profile_info.location_placeholder')"
      />

      <div>
        <b-label class="mb-2 block">{{ t('profile_info.languages') }}</b-label>
        <b-checkbox-group v-model="contactForm.languages">
          <div class="flex flex-wrap gap-2">
            <b-checkbox-chip
              v-for="code in SPOKEN_LANGUAGES"
              :key="code"
              :value="code"
              data-e2e="profile-language-chip"
            >
              {{ t('languages.' + code) }}
            </b-checkbox-chip>
          </div>
        </b-checkbox-group>
      </div>

      <b-divider />

      <b-text-field
        v-model="contactForm.contact_email"
        :label="t('profile_contact.contact_email')"
        :placeholder="t('profile_contact.placeholder_email')"
      />
      <b-text-field
        v-model="contactForm.contact_phone"
        :label="t('profile_contact.contact_phone')"
        :placeholder="t('profile_contact.placeholder_phone')"
      />
      <b-text-field
        v-model="contactForm.whatsapp"
        :label="t('profile_contact.whatsapp')"
        :placeholder="t('profile_contact.placeholder_whatsapp')"
      />
      <b-text-field
        v-model="contactForm.linkedin"
        :label="t('profile_contact.linkedin')"
        :placeholder="t('profile_contact.placeholder_linkedin')"
      />
      <b-divider />

      <b-checkbox-toggle
        :options="privacyOptions"
        v-model="contactForm.visibility"
        class="w-full"
      />

      <div class="flex gap-4 pt-2">
        <b-button block light @click="contactEditModalOpen = false">{{ t('common.cancel') }}</b-button>
        <b-button block @click="onContactSubmit" :loading="contactSaving" :disabled="contactForm.languages.length === 0" data-e2e="profile-modal-save">{{ t('common.save') }}</b-button>
      </div>
    </div>
  </b-modal>
</template>

<script setup lang="ts">
import {computed, ref, onMounted, watch} from 'vue';
import BCard from "~/components/card/BCard.vue";
import BBadge from "~/components/badge/BBadge.vue";
import BAvatar from "~/components/avatar/BAvatar.vue";
import BButton from '../forms/BButton.vue';
import BCheckboxToggle from '../forms/checkbox/BCheckboxToggle.vue';
import BCheckboxGroup from '~/components/forms/checkbox/BCheckboxGroup.vue';
import BCheckboxChip from '~/components/forms/checkbox/BCheckboxChip.vue';
import BLabel from '~/components/forms/BLabel.vue';
import BDivider from "~/components/BDivider.vue";
import {authStore} from "~/stores/auth";
import {useProfileStore} from "~/stores/profileStore";
import {useUserStore} from "~/stores/userStore";
import BModal from "~/components/modal/BModal.vue";
import BTextField from "~/components/forms/BTextField.vue";
import BLocationAutocomplete from "~/components/forms/BLocationAutocomplete.vue";
import BFileUpload from "~/components/forms/BFileUpload.vue";
import { resizeImage } from "~/help/resizeImage";
import { useAlerts } from "~/stores/alert";
import { useFavoriteExperts } from '~/composables/useFavoriteExperts';
import { SPOKEN_LANGUAGES, DEFAULT_LANGUAGES, orderedSpokenLanguages } from "~/help/languages";

const MAX_SKILLS_DISPLAYED = 5;
const MAX_VIDEO_FILE_SIZE = 50 * 1024 * 1024; // 50MB
const MAX_VIDEO_DURATION_SECONDS = 60;

const { t } = useI18n()
const userStore = useUserStore();
const auth = authStore();
const profileStore = useProfileStore();
const avatarUrl = ref('');
const openShareModal = ref(false)
const openContactModal = ref(false)
const route = useRoute()
const skills = ref([])
const showAllSkills = ref(false)
const displayedSkills = computed(() =>
  showAllSkills.value ? skills.value : skills.value.slice(0, MAX_SKILLS_DISPLAYED)
)
const orderedProfileLanguages = computed(() => orderedSpokenLanguages(user.value?.profile?.languages))
const { $api } = useNuxtApp()

const { user } = storeToRefs(userStore);
const { user: me } = storeToRefs(auth);

const { isPreview, toggle } = useProfilePreviewMode();
const isRealOwner = computed(() => me.value && user.value && me.value.id === user.value.id);
const isOwner = computed(() => !isPreview.value && isRealOwner.value);

const hasAnyContact = computed(() => {
  const p = user.value?.profile;
  return !!(p?.contact_phone || p?.whatsapp || p?.contact_email || p?.linkedin);
});

// Favorites
const { isFavorite, toggleFavorite } = useFavoriteExperts();
const canFavorite = computed(() => me.value && !isRealOwner.value);
const isExpertFavorite = computed(() => user.value ? isFavorite(user.value.id) : false);

const onToggleFavorite = () => {
  if (!user.value) return;
  toggleFavorite({
    id: user.value.id,
    handle: user.value.profile?.handle,
    first_name: user.value.first_name,
    last_name: user.value.last_name,
    title: user.value.profile?.title,
    signed_picture_url: user.value.profile?.signed_picture_url,
  });
};

const contactEditModalOpen = ref(false);
const contactSaving = ref(false);
// Ticket 208 : nouvelle photo choisie dans la modale, envoyée seulement au submit.
const selectedPhoto = ref<File | null>(null);
interface ContactForm {
  first_name: string;
  last_name: string;
  title: string;
  daily_rate: string;
  location: string;
  visibility: 'on' | 'off';
  contact_email: string;
  contact_phone: string;
  whatsapp: string;
  linkedin: string;
  languages: string[];
}

const contactForm = ref<ContactForm>({
  first_name: '',
  last_name: '',
  title: '',
  daily_rate: '',
  location: '',
  visibility: 'off',
  contact_email: '',
  contact_phone: '',
  whatsapp: '',
  linkedin: '',
  languages: [...DEFAULT_LANGUAGES],
});

const openContactEditModal = () => {
  const p = user.value?.profile;
  const profileLanguages = p?.languages || [];
  contactForm.value = {
    first_name: user.value?.first_name || '',
    last_name: user.value?.last_name || '',
    title: p?.title || '',
    daily_rate: p?.daily_rate ? String(p.daily_rate) : '',
    location: p?.location || '',
    visibility: p?.is_visible ? 'on' : 'off',
    contact_email: p?.contact_email || '',
    contact_phone: p?.contact_phone || '',
    whatsapp: p?.whatsapp || '',
    linkedin: p?.linkedin || '',
    languages: profileLanguages.length > 0 ? profileLanguages : [...DEFAULT_LANGUAGES],
  };
  selectedPhoto.value = null;
  contactEditModalOpen.value = true;
};

const onContactSubmit = async () => {
  if (!user.value) return;
  contactSaving.value = true;
  try {
    const trimmedRate = contactForm.value.daily_rate.trim();
    await userStore.patchUser({
      first_name: contactForm.value.first_name.trim(),
      last_name: contactForm.value.last_name.trim(),
    });
    await profileStore.updateProfile(user.value.id, {
      title: contactForm.value.title.trim(),
      daily_rate: trimmedRate ? Number(trimmedRate) : null,
      location: contactForm.value.location.trim(),
      is_visible: contactForm.value.visibility === 'on',
      contact_email: contactForm.value.contact_email.trim(),
      contact_phone: contactForm.value.contact_phone.trim(),
      whatsapp: contactForm.value.whatsapp.trim(),
      linkedin: contactForm.value.linkedin.trim(),
      languages: contactForm.value.languages,
    }, { skipGlobalErrorAlert: contactForm.value.visibility === 'on' || contactForm.value.languages.length === 0 });
    if (selectedPhoto.value) {
      try {
        const processedFile = await resizeImage(selectedPhoto.value, 800, 0.8);
        await userStore.uploadAvatar(processedFile);
        selectedPhoto.value = null;
      } catch (photoError) {
        // Endpoint photo distinct (v1 multipart) : les champs texte sont déjà
        // sauvegardés. On signale l'échec photo sans annuler le reste.
        alerts.add({ type: 'danger', title: t('bProfileInformationCard.photoUploadFailed') });
        console.error('Failed to upload profile picture:', photoError);
      }
    }
    const slug = route.params.slug as string;
    await userStore.fetchUser(slug);
    privacyValue.value = contactForm.value.visibility;
    contactEditModalOpen.value = false;
  } catch (error) {
    // Ticket #180: switching visibility to public is blocked when the profile
    // is incomplete; surface the required criteria instead of silently failing.
    if (contactForm.value.visibility === 'on') {
      alerts.add({ type: 'danger', title: t('bProfileInformationCard.cannotBePublic') });
    } else if (contactForm.value.languages.length === 0) {
      // Ticket #271: the save button is already disabled when no language is
      // selected; this mirrors that guard server-side in case it is bypassed.
      alerts.add({ type: 'danger', title: t('languages_errors.profile_languages_empty') });
    }
    console.error('Failed to update contact info:', error);
  } finally {
    contactSaving.value = false;
  }
};

const privacyOptions = [
  { label: t('bProfileInformationCard.private'), value: 'off' },
  { label: t('bProfileInformationCard.public'), value: 'on' }
]

const privacyValue = ref(auth.user?.profile?.is_visible ? 'on' : 'off');

// Computed to determine if profile is public (reactive to toggle changes for owner)
const isProfilePublic = computed(() => {
  if (isOwner.value) {
    return privacyValue.value === 'on';
  }
  return user.value?.profile?.is_visible ?? false;
});

const onPrivacyChange = (_: { label: string, value: 'on' | 'off' }) => {
  if (auth.user) {
    const isVisible = 'on' === _.value;
    profileStore.updateProfile(auth.user.id, { is_visible: isVisible }, { skipGlobalErrorAlert: isVisible }).then(() => {
      if (auth.user.profile) {
        auth.user.profile.is_visible = isVisible;
      }
    }).catch((error) => {
      // Ticket #180: making an incomplete profile public is blocked server-side.
      // Revert the optimistic toggle and explain the required criteria.
      privacyValue.value = isVisible ? 'off' : 'on';
      if (auth.user.profile) {
        auth.user.profile.is_visible = !isVisible;
      }
      if (isVisible) {
        alerts.add({ type: 'danger', title: t('bProfileInformationCard.cannotBePublic') });
      } else {
        console.error('Failed to update profile visibility:', error);
      }
    });
  }
}

onMounted(async () => {
  if (user.value && user.value.profile) {
      avatarUrl.value = user.value.profile.signed_picture_url || '';
      loadUserSkills();
  }
});

watch(() => user.value, (newValue) => {
    if (newValue && newValue.profile) {
        avatarUrl.value = newValue.profile.signed_picture_url || '';
        loadUserSkills();
    }
})

const config = useRuntimeConfig()
const urlToCopy = computed(() => {
    return `${config.public.NUXT_APP_URL}${route.fullPath.substring(1, route.fullPath.length)}`
})

const fullName = computed(() => {
  return user.value ? `${user.value.first_name} ${user.value.last_name}` : '';
});

const initials = computed(() => {
  if (!user.value) return '';
  return (user.value.first_name?.[0] || '') + (user.value.last_name?.[0] || '');
});

const loadUserSkills = () => {
  if (user.value && user.value.profile) {
    $api().v2.skills.list({
      'filter[user_id]': user.value.id
    }).then((response) => {
      skills.value = response
    })
  }
}

// --- Video upload/delete ---
const alerts = useAlerts();
const videoFileInput = ref<HTMLInputElement>();
const isUploading = ref(false);
const isDeletingVideo = ref(false);
const confirmDeleteVideo = ref(false);

const videoUrl = ref(user.value?.profile?.signed_video_url || '');
const hasVideo = computed(() => !!videoUrl.value);

function openVideoUpload() {
  videoFileInput.value?.click();
}

function getVideoDuration(file: File): Promise<number> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(video.src);
      resolve(video.duration);
    };
    video.onerror = () => {
      URL.revokeObjectURL(video.src);
      reject(new Error('Failed to load video metadata'));
    };
    video.src = URL.createObjectURL(file);
  });
}

async function onVideoFileSelected(event: Event) {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  if (!file) return;

  input.value = '';

  const allowedTypes = ['video/mp4', 'video/webm', 'video/quicktime'];
  if (!allowedTypes.includes(file.type)) {
    alerts.add({ type: 'danger', title: t('profile_video.format_error') });
    return;
  }

  if (file.size > MAX_VIDEO_FILE_SIZE) {
    alerts.add({ type: 'danger', title: t('profile_video.size_error') });
    return;
  }

  try {
    const duration = await getVideoDuration(file);
    if (duration > MAX_VIDEO_DURATION_SECONDS) {
      alerts.add({ type: 'danger', title: t('profile_video.duration_error') });
      return;
    }
  } catch {
    // If we can't read duration, let the server handle it
  }

  isUploading.value = true;
  try {
    await profileStore.uploadPresentationVideo(user.value.id, file);
    alerts.add({ type: 'success', title: t('profile_video.upload_success') });
    await userStore.fetchUser(user.value.profile.slug);
    videoUrl.value = user.value?.profile?.signed_video_url || '';
  } catch (e: any) {
    console.error('Video upload failed:', e);
    const status = e?.response?.status ?? e?.statusCode;
    if (status === 413) {
      alerts.add({ type: 'danger', title: t('profile_video.size_error') });
    } else if (status === 400) {
      alerts.add({ type: 'danger', title: t('profile_video.format_error') });
    } else {
      alerts.add({ type: 'danger', title: t('profile_video.upload_error') });
    }
  } finally {
    isUploading.value = false;
  }
}

async function onDeleteVideo() {
  isDeletingVideo.value = true;
  try {
    await profileStore.deletePresentationVideo(user.value.id);
    confirmDeleteVideo.value = false;
    await userStore.fetchUser(user.value.profile.slug);
    videoUrl.value = '';
    alerts.add({ type: 'success', title: t('profile_video.delete_success') });
  } catch (e) {
    console.error('Video delete failed:', e);
    alerts.add({ type: 'danger', title: t('profile_video.delete_error') });
  } finally {
    isDeletingVideo.value = false;
  }
}
</script>
